import { HttpErrorResponse, HttpInterceptorFn } from "@angular/common/http";
import { inject } from "@angular/core";
import { Router } from "@angular/router";
import { from, throwError } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";
import { environment } from "../../../environments/environment";
import { CognitoService } from "../services/cognito.service";
import { ToastService } from "../services/toast.service";

// AdminGuard's 403 messages. Other 403s from des-api are business answers ("No active
// subscription found", ownership checks) and must not end the session.
const ADMIN_AUTH_403_MESSAGES = ["Admin access required", "Authentication required"];

// Login calls these itself and handles their failures, so a 401/403 there is not a lapsed session.
const LOGIN_PATHS = ["/auth/validate-password", "/auth/admin/me"];

// One sign-out for a burst of failing requests (a page often fires several at once).
let signingOut = false;

export const AuthInterceptor: HttpInterceptorFn = (req, next) => {
  const isS3PresignedUrl = isPresignedS3Url(req.url);

  if (isS3PresignedUrl || isStageViperRecapRequest(req.url)) {
    return next(req);
  }

  const cognitoService = inject(CognitoService);
  const router = inject(Router);
  const toastService = inject(ToastService);

  return from(cognitoService.getAuthToken()).pipe(
    switchMap((token) => {
      if (token) {
        const authReq = req.clone({
          setHeaders: {
            Authorization: `Bearer ${token}`,
          },
        });
        return next(authReq);
      }
      return next(req);
    }),
    catchError((error: unknown) => {
      // Already on /login: a late failure from the page just left must not toast a second time.
      if (isSessionRejected(req.url, error) && !signingOut && !router.url.startsWith("/login")) {
        signingOut = true;
        void endSession(cognitoService, router, toastService);
      }
      return throwError(() => error);
    }),
  );
};

/**
 * Signs out, returns to the login page and says why. Never retries the request.
 */
async function endSession(
  cognitoService: CognitoService,
  router: Router,
  toastService: ToastService,
): Promise<void> {
  try {
    await cognitoService.signOut();
  } catch (error) {
    console.error("Sign-out after rejected session failed:", error);
  }
  try {
    await router.navigate(["/login"]);
    toastService.error("Please sign in again.", "Signed out");
  } finally {
    signingOut = false;
  }
}

/**
 * True when des-api refused the session itself: any 401, or a 403 from AdminGuard.
 */
function isSessionRejected(url: string, error: unknown): boolean {
  if (!(error instanceof HttpErrorResponse)) return false;
  if (!url.startsWith(environment.apiBasePath)) return false;
  const path = url.split("?")[0];
  if (LOGIN_PATHS.some((login) => path === `${environment.apiBasePath}${login}`)) return false;
  if (error.status === 401) return true;
  return error.status === 403 && ADMIN_AUTH_403_MESSAGES.includes(errorMessage(error));
}

/**
 * des-api's exception filter puts the HttpException response in `message`: a string, or
 * Nest's `{ message, error, statusCode }` object.
 */
function errorMessage(error: HttpErrorResponse): string {
  const message: unknown = error.error?.message;
  if (typeof message === "string") return message;
  if (message && typeof message === "object" && "message" in message) {
    const inner = (message as { message: unknown }).message;
    return typeof inner === "string" ? inner : "";
  }
  return "";
}

function isStageViperRecapRequest(url: string): boolean {
  return environment.viperRecapAuthDisabled && url.includes("/api/admin/viper/recap");
}

function isPresignedS3Url(url: string): boolean {
  try {
    const urlObj = new URL(url);

    const isS3 = urlObj.hostname.includes('amazonaws.com') &&
      (urlObj.hostname.includes('s3') || urlObj.hostname.includes('s3-'));

    const hasV4Signature = urlObj.searchParams.has('X-Amz-Signature');
    const hasV2Signature = urlObj.searchParams.has('Signature') &&
      urlObj.searchParams.has('Expires');

    return isS3 && (hasV4Signature || hasV2Signature);
  } catch {
    return url.includes('amazonaws.com') &&
      (url.includes('X-Amz-Signature') ||
        (url.includes('Signature=') && url.includes('Expires=')));
  }
}
